import AbstractService, {
  ServiceAuth,
  ModelUser
} from "./AbstractService";
import {
  WebAuthnCreationOptionsJson,
  WebAuthnRequestOptionsJson,
  WebAuthnCredentialJson
} from "../utils/webauthn";

/**
 * the second factors the backend offers for a given challenge
 */
export type TwoFactorMethod = 'totp' | 'webauthn' | 'recovery';

/**
 * the bearer token part of every successful authentication
 */
export interface AuthorisationToken {
  token: string;
  type: string;
}

/**
 * the unchanged success answer of /auth/login, shared by every verify endpoint.
 * device_token is only present when remember_device was requested
 */
export interface LoginSuccessResponse {
  status: 'success';
  user: ModelUser;
  impersonating?: boolean;
  impersonation_id?: string | null;
  authorisation: AuthorisationToken;
  device_token?: string;
}

/**
 * the answer of /auth/login when a second factor is still missing. http 200 -
 * this is not an error case and must not be treated as one
 */
export interface TwoFactorRequiredResponse {
  status: 'two_factor_required';
  challenge_token: string;
  methods: TwoFactorMethod[];
  expires_in: number;
}

/**
 * what login() can answer. which of the two it is, is decided by the caller
 */
export type LoginResponse = LoginSuccessResponse | TwoFactorRequiredResponse;

/**
 * a registered passkey as it appears in the self service list
 */
export interface TwoFactorPasskey {
  id: string;
  name: string;
  created_at: string | null;
  last_used_at: string | null;
}

/**
 * a device the user marked as trusted
 */
export interface TwoFactorTrustedDevice {
  id: string;
  label: string | null;
  last_used_at: string | null;
  expires_at: string | null;
}

/**
 * everything /user/two-factor reports about the logged in user
 */
export interface TwoFactorStatus {
  totp: {
    enabled: boolean;
    confirmed_at: string | null;
  };
  passkeys: TwoFactorPasskey[];
  recovery_codes: {
    total: number;
    remaining: number;
  };
  trusted_devices: TwoFactorTrustedDevice[];
}

/**
 * the freshly generated totp secret, before it is confirmed
 */
export interface TotpSetup {
  secret: string;
  otpauth_uri: string;
}

/**
 * recovery codes are delivered in clear text exactly once, at generation
 */
export interface RecoveryCodes {
  recovery_codes: string[];
}

/**
 * the plain acknowledgement the write endpoints answer with
 */
export interface TwoFactorAcknowledgement {
  status: string;
  message?: string;
}

/**
 * webauthn registration options plus the challenge token that belongs to them.
 * the token has to travel back to /user/two-factor/passkeys, so it is kept
 * together with the options instead of being handed around separately
 */
export interface PasskeyRegistrationChallenge {
  challengeToken: string;
  publicKey: WebAuthnCreationOptionsJson;
}

/**
 * webauthn assertion options plus the challenge token they belong to. for the
 * second factor the token is the one login() already returned, for the
 * passwordless path it is created by the options call itself
 */
export interface PasskeyAssertionChallenge {
  challengeToken: string;
  publicKey: WebAuthnRequestOptionsJson;
}

/**
 * isTwoFactorRequired
 * type guard so the consumer can branch without touching string literals
 * @param response
 */
export function isTwoFactorRequired(response: LoginResponse): response is TwoFactorRequiredResponse {
  return !!response && response.status === 'two_factor_required';
}

/**
 * TwoFactorService
 *
 * the api package has no two factor service, so every endpoint is called through
 * the shared http service of the existing auth service. that also means the
 * bearer token, the jwt refresh and the central loading indicator keep working.
 *
 * errors are never caught in here - the consumer does the try/catch and needs the
 * status code (401 wrong code, 410 challenge gone, 429 too many attempts).
 */
export default class TwoFactorService extends AbstractService {
  /**
   * ServiceAuth
   */
  static service: ServiceAuth;

  /**
   * constructor
   */
  constructor() {
    super();

    TwoFactorService.service = this.getApi().getServiceAuth();
  }

  /**
   * login
   *
   * deliberately NOT serviceAuth.login(): that implementation reads
   * response.data.authorisation.token unconditionally and throws a TypeError on
   * the two_factor_required answer, which would turn a correct password into an
   * unexplainable crash.
   *
   * the raw answer is returned. whether it is a finished login or a challenge is
   * decided by the caller, and only the caller knows whether it wants to store a
   * session (see applySession).
   *
   * @param email
   * @param password
   * @param deviceToken remembered device, skips the second factor when still valid
   */
  login(email: string, password: string, deviceToken?: string): Promise<LoginResponse> {
    const body: { [key: string]: string } = {
      email: email,
      password: password
    };

    /**
     * only sent when there is one, so a missing token never looks like an
     * invalid one to the backend
     */
    if (deviceToken) {
      body.device_token = deviceToken;
    }

    return TwoFactorService.service.getHttpService()
      .post('/auth/login', body)
      .then((response) => response.data as LoginResponse);
  }

  /**
   * verifyTotp
   * @param challengeToken
   * @param code
   * @param rememberDevice
   */
  verifyTotp(
    challengeToken: string,
    code: string,
    rememberDevice: boolean = false
  ): Promise<LoginSuccessResponse> {
    return this.verify('/auth/two-factor/verify', challengeToken, code, rememberDevice);
  }

  /**
   * verifyRecovery
   * a recovery code is spent by this call even when it succeeds only once
   * @param challengeToken
   * @param code
   * @param rememberDevice
   */
  verifyRecovery(
    challengeToken: string,
    code: string,
    rememberDevice: boolean = false
  ): Promise<LoginSuccessResponse> {
    return this.verify('/auth/two-factor/recovery', challengeToken, code, rememberDevice);
  }

  /**
   * webauthnAssertionOptions
   * second factor: the user is already known through the challenge token
   * @param challengeToken
   */
  webauthnAssertionOptions(challengeToken: string): Promise<PasskeyAssertionChallenge> {
    return TwoFactorService.service.getHttpService()
      .post('/auth/two-factor/webauthn/options', { challenge_token: challengeToken })
      .then((response) => {
        const challenge = TwoFactorService.normalizeChallenge(response.data);

        return {
          /**
           * the second factor keeps the token it was given, a token in the
           * answer only overrides it when the backend really rotated it
           */
          challengeToken: challenge.challengeToken || challengeToken,
          publicKey: challenge.publicKey as WebAuthnRequestOptionsJson
        };
      });
  }

  /**
   * webauthnAssertionVerify
   * @param challengeToken
   * @param credential result of getCredential()
   * @param rememberDevice
   */
  webauthnAssertionVerify(
    challengeToken: string,
    credential: WebAuthnCredentialJson,
    rememberDevice: boolean = false
  ): Promise<LoginSuccessResponse> {
    return TwoFactorService.service.getHttpService()
      .post('/auth/two-factor/webauthn/verify', {
        challenge_token: challengeToken,
        credential: credential,
        remember_device: rememberDevice
      })
      .then((response) => response.data as LoginSuccessResponse);
  }

  /**
   * passwordlessOptions
   * completely unauthenticated first factor. the user is only established by the
   * assertion, so the challenge token is created here and has no user attached
   * yet
   */
  passwordlessOptions(): Promise<PasskeyAssertionChallenge> {
    return TwoFactorService.service.getHttpService()
      .post('/auth/passkey/options', {})
      .then((response) => {
        const challenge = TwoFactorService.normalizeChallenge(response.data);

        return {
          challengeToken: challenge.challengeToken,
          publicKey: challenge.publicKey as WebAuthnRequestOptionsJson
        };
      });
  }

  /**
   * passwordlessVerify
   * @param challengeToken
   * @param credential result of getCredential()
   * @param rememberDevice
   */
  passwordlessVerify(
    challengeToken: string,
    credential: WebAuthnCredentialJson,
    rememberDevice: boolean = false
  ): Promise<LoginSuccessResponse> {
    return TwoFactorService.service.getHttpService()
      .post('/auth/passkey/verify', {
        challenge_token: challengeToken,
        credential: credential,
        remember_device: rememberDevice
      })
      .then((response) => response.data as LoginSuccessResponse);
  }

  /**
   * applySession
   *
   * stores what a successful login or verify handed back. it lives here so no
   * component has to know that the token and the user are two separate calls in
   * the api package, and so the order stays the same everywhere: token first,
   * because everything that reacts to the user being set already needs it.
   *
   * @param user
   * @param token
   */
  applySession(user: ModelUser | any, token: string): void {
    TwoFactorService.service.setToken(token);

    /**
     * the jwt manager keeps its own copy of the token, and isLoggedIn() asks it
     * first: "jwtManager ? jwtManager.isTokenValid() && getToken() : getToken()".
     * setToken() above only fills the service's own storage, so without this the
     * manager stays empty, isTokenValid() is false and ProtectedRoute bounces the
     * user straight back to the login screen with a perfectly good token in hand.
     * ServiceAuth.login() in the api package does exactly this - we have to
     * repeat it because we cannot use that method (it assumes the response always
     * carries a token and blows up on two_factor_required).
     */
    const jwtManager = TwoFactorService.service.getJWTManager();

    if (jwtManager) {
      jwtManager.setToken(token);
    }

    TwoFactorService.service.setUser(user);
  }

  /**
   * getStatus
   * everything the self service screen shows in one call
   */
  getStatus(): Promise<TwoFactorStatus> {
    return TwoFactorService.service.getHttpService()
      .get('/user/two-factor')
      .then((response) => TwoFactorService.normalizeStatus(response.data));
  }

  /**
   * setupTotp
   * creates the secret. it is NOT active yet - only confirmTotp arms it
   */
  setupTotp(): Promise<TotpSetup> {
    return TwoFactorService.service.getHttpService()
      .post('/user/two-factor/totp', {})
      .then((response) => {
        const data = TwoFactorService.asObject(response.data);

        return {
          secret: data.secret || '',
          otpauth_uri: data.otpauth_uri || ''
        };
      });
  }

  /**
   * confirmTotp
   * arms the secret and answers with the recovery codes - the only moment they
   * exist in clear text
   * @param code
   */
  confirmTotp(code: string): Promise<RecoveryCodes> {
    return TwoFactorService.service.getHttpService()
      .post('/user/two-factor/totp/confirm', { code: code })
      .then((response) => TwoFactorService.normalizeRecoveryCodes(response.data));
  }

  /**
   * disableTotp
   * @param currentPassword
   */
  disableTotp(currentPassword: string): Promise<TwoFactorAcknowledgement> {
    /**
     * DELETE with a body, see methodOverride: the password must not end up in
     * the url
     */
    return TwoFactorService.service.getHttpService()
      .post(
        '/user/two-factor/totp',
        { current_password: currentPassword },
        undefined,
        TwoFactorService.methodOverride('DELETE')
      )
      .then((response) => TwoFactorService.normalizeAcknowledgement(response.data));
  }

  /**
   * regenerateRecoveryCodes
   * invalidates every remaining code and answers with the new set in clear text
   * @param currentPassword
   */
  regenerateRecoveryCodes(currentPassword: string): Promise<RecoveryCodes> {
    return TwoFactorService.service.getHttpService()
      .post('/user/two-factor/recovery-codes', { current_password: currentPassword })
      .then((response) => TwoFactorService.normalizeRecoveryCodes(response.data));
  }

  /**
   * passkeyRegistrationOptions
   * the options are fed to createCredential(), the challenge token comes back in
   * registerPasskey()
   */
  passkeyRegistrationOptions(): Promise<PasskeyRegistrationChallenge> {
    return TwoFactorService.service.getHttpService()
      .post('/user/two-factor/passkeys/options', {})
      .then((response) => {
        const challenge = TwoFactorService.normalizeChallenge(response.data);

        return {
          challengeToken: challenge.challengeToken,
          publicKey: challenge.publicKey as WebAuthnCreationOptionsJson
        };
      });
  }

  /**
   * registerPasskey
   * @param challengeToken
   * @param credential result of createCredential()
   * @param name the label the user gave the key, several keys are the point
   */
  registerPasskey(
    challengeToken: string,
    credential: WebAuthnCredentialJson,
    name: string
  ): Promise<TwoFactorPasskey> {
    return TwoFactorService.service.getHttpService()
      .post('/user/two-factor/passkeys', {
        challenge_token: challengeToken,
        credential: credential,
        name: name
      })
      .then((response) => TwoFactorService.normalizePasskey(TwoFactorService.unwrap(response.data)));
  }

  /**
   * renamePasskey
   * @param id
   * @param name
   */
  renamePasskey(id: string, name: string): Promise<TwoFactorPasskey> {
    /**
     * PATCH, see methodOverride
     */
    return TwoFactorService.service.getHttpService()
      .post(
        '/user/two-factor/passkeys/' + encodeURIComponent(id),
        { name: name },
        undefined,
        TwoFactorService.methodOverride('PATCH')
      )
      .then((response) => TwoFactorService.normalizePasskey(TwoFactorService.unwrap(response.data)));
  }

  /**
   * deletePasskey
   * @param id
   * @param currentPassword
   */
  deletePasskey(id: string, currentPassword: string): Promise<TwoFactorAcknowledgement> {
    /**
     * DELETE with a body, see methodOverride
     */
    return TwoFactorService.service.getHttpService()
      .post(
        '/user/two-factor/passkeys/' + encodeURIComponent(id),
        { current_password: currentPassword },
        undefined,
        TwoFactorService.methodOverride('DELETE')
      )
      .then((response) => TwoFactorService.normalizeAcknowledgement(response.data));
  }

  /**
   * forgetTrustedDevices
   * drops every remembered device at once, so the next login asks for the second
   * factor everywhere. no body, so the plain delete of the http service fits
   */
  forgetTrustedDevices(): Promise<TwoFactorAcknowledgement> {
    return TwoFactorService.service.getHttpService()
      .delete('/user/two-factor/trusted-devices')
      .then((response) => TwoFactorService.normalizeAcknowledgement(response.data));
  }

  /**
   * changePassword
   * @param currentPassword
   * @param password
   * @param passwordConfirmation
   */
  changePassword(
    currentPassword: string,
    password: string,
    passwordConfirmation: string
  ): Promise<TwoFactorAcknowledgement> {
    return TwoFactorService.service.getHttpService()
      .put('/user/password', {
        current_password: currentPassword,
        password: password,
        password_confirmation: passwordConfirmation
      })
      .then((response) => TwoFactorService.normalizeAcknowledgement(response.data));
  }

  /**
   * verify
   * totp and recovery differ in the endpoint only
   * @param endpoint
   * @param challengeToken
   * @param code
   * @param rememberDevice
   */
  private verify(
    endpoint: string,
    challengeToken: string,
    code: string,
    rememberDevice: boolean
  ): Promise<LoginSuccessResponse> {
    return TwoFactorService.service.getHttpService()
      .post(endpoint, {
        challenge_token: challengeToken,
        code: code,
        remember_device: rememberDevice
      })
      .then((response) => response.data as LoginSuccessResponse);
  }

  /**
   * methodOverride
   *
   * the shared http service of @dia-ui/util_http-service has no patch() at all
   * and its delete() only takes query parameters, never a body. putting
   * current_password into the query string is out of the question, so those two
   * calls go out as POST carrying the real verb in the header symfony evaluates
   * before routing (Request::getMethod). the backend needs no change for it and
   * config/cors.php allows every header.
   *
   * @param method
   */
  private static methodOverride(method: string): { [key: string]: string } {
    return { 'X-HTTP-Method-Override': method };
  }

  /**
   * asObject
   * php serializes an empty associative array as [], and a missing key as
   * nothing at all - both have to end up as an empty object here
   * @param value
   */
  private static asObject(value: any): { [key: string]: any } {
    return (value && typeof value === 'object' && !Array.isArray(value)) ? value : {};
  }

  /**
   * asArray
   * @param value
   */
  private static asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }

  /**
   * unwrap
   * write endpoints in this project answer either with the record itself or with
   * it wrapped in data/passkey - both are accepted
   * @param value
   */
  private static unwrap(value: any): { [key: string]: any } {
    const payload = TwoFactorService.asObject(value);

    if (payload.passkey) {
      return TwoFactorService.asObject(payload.passkey);
    }

    if (payload.data) {
      return TwoFactorService.asObject(payload.data);
    }

    return payload;
  }

  /**
   * normalizeChallenge
   *
   * the webauthn options endpoints deliver the options and the challenge token
   * together. whether the token sits next to the options or the options sit in a
   * publicKey/options wrapper is not worth a second round trip to find out, so
   * both layouts are accepted and the token is stripped from the options - the
   * browser would ignore it, but it has no business being there.
   *
   * @param data
   */
  private static normalizeChallenge(data: any): { challengeToken: string; publicKey: any } {
    const payload = TwoFactorService.asObject(data),
      wrapped = payload.publicKey || payload.options,
      source = TwoFactorService.asObject(wrapped || payload),
      publicKey: { [key: string]: any } = {};

    Object.keys(source).forEach((key) => {
      if (key !== 'challenge_token' && key !== 'challengeToken' && key !== 'status') {
        publicKey[key] = source[key];
      }
    });

    return {
      challengeToken: payload.challenge_token || payload.challengeToken
        || source.challenge_token || source.challengeToken || '',
      publicKey: publicKey
    };
  }

  /**
   * normalizeStatus
   * @param data
   */
  private static normalizeStatus(data: any): TwoFactorStatus {
    const payload = TwoFactorService.asObject(data),
      totp = TwoFactorService.asObject(payload.totp),
      codes = TwoFactorService.asObject(payload.recovery_codes);

    return {
      totp: {
        /**
         * a secret that exists but was never confirmed does not count as active,
         * so enabled is trusted only when the backend really says true
         */
        enabled: totp.enabled === true,
        confirmed_at: totp.confirmed_at || null
      },
      passkeys: TwoFactorService.asArray(payload.passkeys)
        .map((passkey) => TwoFactorService.normalizePasskey(passkey)),
      recovery_codes: {
        total: Number(codes.total) || 0,
        remaining: Number(codes.remaining) || 0
      },
      trusted_devices: TwoFactorService.asArray(payload.trusted_devices)
        .map((device): TwoFactorTrustedDevice => {
          const entry = TwoFactorService.asObject(device);

          return {
            id: entry.id || '',
            label: entry.label || null,
            last_used_at: entry.last_used_at || null,
            expires_at: entry.expires_at || null
          };
        })
    };
  }

  /**
   * normalizePasskey
   * @param data
   */
  private static normalizePasskey(data: any): TwoFactorPasskey {
    const entry = TwoFactorService.asObject(data);

    return {
      id: entry.id || '',
      name: entry.name || '',
      created_at: entry.created_at || null,
      last_used_at: entry.last_used_at || null
    };
  }

  /**
   * normalizeRecoveryCodes
   * @param data
   */
  private static normalizeRecoveryCodes(data: any): RecoveryCodes {
    const payload = TwoFactorService.asObject(data);

    return {
      recovery_codes: TwoFactorService.asArray(payload.recovery_codes)
        .map((code) => String(code))
    };
  }

  /**
   * normalizeAcknowledgement
   * @param data
   */
  private static normalizeAcknowledgement(data: any): TwoFactorAcknowledgement {
    const payload = TwoFactorService.asObject(data);

    return {
      /**
       * a 2xx without a body is a success as well - the http service would have
       * rejected anything else
       */
      status: payload.status || 'success',
      message: payload.message
    };
  }
}
